/**
 * D146 (item 14, 9 Oct 2026 QA retest): a client invoice with a credit note against it shows
 * "credited" / "partially credited" as well as its normal lifecycle status (draft/sent/paid/...,
 * `clientInvoiceStatus.ts`) — it never REPLACES it. That status is a separate owner (D96.1-3:
 * what moves are allowed, what a sent invoice locks) and stays exactly what it was; this is a
 * second, independent fact about the same invoice, read the same way the header's existing "Net
 * after credits" figure already is (invoiceGrandTotal + the sum of non-cancelled credit notes'
 * own grandTotal, which is stored negative — BUSINESS_RULES#client-invoice-credited).
 *
 * Basis, decided here because none existed (D146): MONEY, against the invoice's own grand total —
 * the same basis "Net after credits" already uses, not a count of lines or units (that is a
 * different, already-contested question — item 37, deliberately left alone this pass). Rounded to
 * the cent so a one-cent rounding remainder never reads as "partially credited" forever.
 */
import { round2 } from '../../utils/invoiceLineMath';

export type CreditStatus = 'credited' | 'partially credited' | null;

export function creditStatusFor(
  invoiceGrandTotal: number | null | undefined,
  creditedTotal: number | null | undefined
): CreditStatus {
  const total = typeof invoiceGrandTotal === 'number' && Number.isFinite(invoiceGrandTotal) ? invoiceGrandTotal : 0;
  const creditedRaw = typeof creditedTotal === 'number' && Number.isFinite(creditedTotal) ? creditedTotal : 0;
  const credited = Math.abs(round2(creditedRaw));
  if (credited <= 0) return null;
  if (total <= 0) return 'credited';
  return credited >= round2(total - 0.01) ? 'credited' : 'partially credited';
}
